import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { clsx } from 'clsx'
import * as fuzzySort from 'fuzzysort'

import { getAvailableTimezoneItems } from '@bettermode/common/timezone'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { Dropdown, DropdownProps } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Input } from '@tribeplatform/react-ui-kit/Input'

import { T, useI18n } from '../../i18n/index.js'

interface TimezoneOption {
  value: string
  text: string
  subText: string
}

const POPULAR_TIMEZONE_VALUES = [
  'America/Los_Angeles',
  'America/Chicago',
  'America/Toronto',
  'America/New_York',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
] as const

export interface TimezonePickerProps {
  dropdownPosition?: DropdownProps['placement']
  value: string
  onChange: (timezone: string) => void
  placeholder?: string
  withPopularTimezones?: boolean
  popularTimezones?: string[]
}

export const TimezonePicker = ({
  value,
  onChange,
  placeholder,
  withPopularTimezones = true,
  popularTimezones = POPULAR_TIMEZONE_VALUES as unknown as string[],
  dropdownPosition,
}: TimezonePickerProps) => {
  const { $t } = useI18n()
  const [query, setQuery] = useState('')

  const timezoneOptions = useMemo<TimezoneOption[]>(
    () => getAvailableTimezoneItems(),
    [],
  )

  const popularTimezonesOptions = useMemo(
    () =>
      popularTimezones
        .map(tz => timezoneOptions.find(option => option.value === tz))
        .filter((tz): tz is TimezoneOption => tz !== undefined),
    [timezoneOptions, popularTimezones],
  )

  const filteredOptions = useMemo(() => {
    if (!query) {
      return []
    }
    return fuzzySort
      .go(query, timezoneOptions, { key: 'text', threshold: -10000 })
      .map(result => result.obj)
  }, [query, timezoneOptions])

  const selectedOption = useMemo(
    () => timezoneOptions.find(option => option.value === value),
    [timezoneOptions, value],
  )

  const displayText = selectedOption?.text ?? placeholder

  const placeholderText =
    placeholder ??
    $t({
      defaultMessage: 'Search for a timezone',
      id: 'TimezonePicker.Placeholder',
    })

  const showSearchResults = query.length > 0

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value)
    },
    [],
  )

  return (
    <Dropdown placement={dropdownPosition} className="w-full">
      <>
        <Dropdown.ButtonMinimal as="div" className="w-full">
          <Input
            className="w-full"
            value={displayText}
            placeholder={placeholderText}
            trailingAddon={
              <SvgIcon
                name="selector"
                className="text-content-subdued"
                size="md"
              />
            }
            /** this is just a read only input. Passing the onChange to avoid console errors */
            onChange={() => null}
          />
        </Dropdown.ButtonMinimal>

        <Dropdown.Items className="p-0 overflow-hidden">
          <div className="pb-2 border-b border-line">
            <TimezoneSearchInput
              value={query}
              onChange={handleSearchChange}
              placeholder={placeholderText}
            />
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {!showSearchResults && withPopularTimezones && (
              <>
                <TimezonePickerItemList
                  options={popularTimezonesOptions}
                  title={
                    <T
                      id="TimezonePicker.PopularTimezones"
                      defaultMessage="Popular Timezones"
                    />
                  }
                  selectedValue={value}
                  onSelect={onChange}
                />
                <Divider padding="sm" />
              </>
            )}

            {!showSearchResults && (
              <TimezonePickerItemList
                options={timezoneOptions}
                title={
                  <T
                    id="TimezonePicker.AllTimezones"
                    defaultMessage="All Timezones"
                  />
                }
                selectedValue={value}
                onSelect={onChange}
              />
            )}

            {showSearchResults && (
              <TimezonePickerItemList
                options={filteredOptions}
                title={
                  <T
                    id="TimezonePicker.SearchResults"
                    defaultMessage="Search Results"
                  />
                }
                selectedValue={value}
                onSelect={onChange}
              />
            )}

            {showSearchResults && filteredOptions.length === 0 && (
              <div className="px-4 py-8 text-center text-content-subdued text-sm">
                <T
                  id="TimezonePicker.NoResults"
                  defaultMessage="No timezones found"
                />
              </div>
            )}
          </div>
        </Dropdown.Items>
      </>
    </Dropdown>
  )
}

interface TimezoneSearchInputProps {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
}

const TimezoneSearchInput = ({
  value,
  onChange,
  placeholder,
}: TimezoneSearchInputProps) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    /**
     * The dropdown is opened with AnimatePortal which animates the content in.
     * So we need to wait for the animation to complete before focusing the input.
     */
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus({ preventScroll: true })
      }
    })
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      /**
       * Since this input is inside the dropdown, need to prevent keydown events
       * to avoid issues like pressing SpaceBar selecting the item in the dropdown.
       * But allow navigation keys (arrows, Enter, Escape, Tab) to propagate
       * so the dropdown can handle them for keyboard navigation.
       */
      const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab']
      if (!navigationKeys.includes(e.key)) {
        e.stopPropagation()
      }
    },
    [],
  )

  return (
    <Input
      ref={ref}
      leadingAddon={<SvgIcon name="search" size="md" />}
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
    />
  )
}

interface TimezonePickerItemListProps {
  options: TimezoneOption[]
  title: ReactNode
  selectedValue: string
  onSelect: (value: string) => void
}

const TimezonePickerItemList = ({
  options,
  title,
  selectedValue,
  onSelect,
}: TimezonePickerItemListProps) => (
  <div className="flex flex-col gap-y-2">
    <div className="text-content-muted text-sm px-2 pt-2">{title}</div>
    <div className="flex flex-col gap-y-1">
      {options.map(option => (
        <TimezonePickerItem
          key={option.value}
          option={option}
          isSelected={option.value === selectedValue}
          onSelect={onSelect}
        />
      ))}
    </div>
  </div>
)

interface TimezonePickerItemProps {
  option: TimezoneOption
  isSelected: boolean
  onSelect: (value: string) => void
}

const TimezonePickerItem = ({
  option,
  isSelected,
  onSelect,
}: TimezonePickerItemProps) => {
  const handleClick = useCallback(() => {
    onSelect(option.value)
  }, [onSelect, option.value])

  return (
    <Dropdown.Item
      onClick={handleClick}
      className={clsx(
        'px-1 !py-2',
        'hover:bg-surface-hovered cursor-pointer',
        isSelected && 'bg-surface-hovered',
      )}
    >
      <div className="flex items-center justify-between w-full gap-x-3">
        <span
          className={clsx(
            'text-sm',
            isSelected ? 'text-content font-medium' : 'text-content',
          )}
        >
          {option.text}
        </span>
        <span className="text-sm text-content-muted shrink-0">
          {option.subText}
        </span>
      </div>
    </Dropdown.Item>
  )
}
